import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ImageProfile } from '@cds/betting-offer';
import { EpcotConfig, PriceBoostConfig } from '@frontend/sports/common/client-config-data-access';
import { ListItemComponent } from '@frontend/sports/common/ui/sports-list-items';
import { CompetitionLogoComponent, CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { ItemComponent, TreeItem, TreeSeparatorItem } from '@frontend/sports/item/feature';
import { PriceboostCountSignpostComponent } from '@frontend/sports/price-boost/feature';
import { TopItemsPayload, TopItemsType } from '@frontend/sports/types/components/widget/types';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { DsDivider } from '@frontend/ui/divider';
import { ReplaySubject, combineLatest, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs';

import { BadgeComponent } from '../crm-promotion/badge.component';
import { CrmPromotionProviderService, PromotionMap } from '../crm-promotion/crm-promotion-provider.service';
import { CrmPromotionModule } from '../crm-promotion/crm-promotion.module';
import { FavouriteLeagueToggleComponent } from '../favourites/toggle/favourite-league-toggle.component';
import { LeagueTreeItem, VirtualGroupTreeItem } from './competition-list.models';
import { TopItemsTrackingService } from './top-items-tracking.service';
import { TopItemsWidgetService } from './top-items-widget.service';

@Component({
    selector: 'ms-top-items-widget',
    templateUrl: './top-items-widget.component.html',
    styleUrls: ['./top-items-widget.component.scss'],
    providers: [TopItemsTrackingService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        AsyncPipe,
        ItemComponent,
        BadgeComponent,
        NgTemplateOutlet,
        CrmPromotionModule,
        CompetitionLogoComponent,
        PriceboostCountSignpostComponent,
        FavouriteLeagueToggleComponent,
        ListItemComponent,
        DsDivider,
    ],
})
export class TopItemsWidgetComponent extends WidgetComponent<TopItemsPayload> implements OnDestroy {
    @HostBinding('class') className = 'top-items-widget list list-card';

    @HostBinding('class.epcot-enabled')
    get epcotEnabledClass() {
        return this.isEpcotEnabled;
    }

    isEpcotEnabled = false;

    private readonly widgetPayload$ = new ReplaySubject<TopItemsPayload>();

    private readonly promoMap$ = this.widgetPayload$.pipe(
        map((d) => d.type),
        distinctUntilChanged(),
        switchMap((type) => this.crmPromotionProvider.promotionsMap$.pipe(map((promoMap) => this.getPromotionsForType(promoMap, type)))),
        startWith({}),
    );

    private readonly tree$ = this.widgetPayload$.pipe(
        switchMap((data) => this.topItemsService.buildItemTree(data, this.route.snapshot.params.sport)),
    );

    readonly viewData$ = combineLatest([this.tree$, this.promoMap$]).pipe(
        tap(([topItems, _]) => {
            this.hasData = !!topItems.tree?.children.length;
            this.setWidgetLoaded();
        }),
        map(([topItems, promoMap]) => ({
            tree: topItems.tree,
            type: topItems.type,
            promoMap,
        })),
    );

    constructor(
        private topItemsService: TopItemsWidgetService,
        private route: ActivatedRoute,
        private trackingService: TopItemsTrackingService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private competitionLogoService: CompetitionLogosService,
        private priceBoostConfig: PriceBoostConfig,
        private epcotConfig: EpcotConfig,
    ) {
        super();
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    protected override onData(data: TopItemsPayload): void {
        this.widgetPayload$.next(data);
    }

    moreClicked(type: TopItemsType): void {
        this.trackingService.trackMoreClicked(type);
    }

    itemClicked(item: TreeItem, type: TopItemsType, index: number): void {
        this.trackingService.trackItemClicked(item, type, index);
    }

    hasCompetitionLogo(sportId: number, leagueId: number, imageProfile: ImageProfile): boolean {
        return !!imageProfile || this.competitionLogoService.hasLogo(sportId, leagueId);
    }

    isLeagueOrVirtualGroup(item: TreeItem): boolean {
        return item instanceof LeagueTreeItem || item instanceof VirtualGroupTreeItem;
    }

    getCount(item: TreeItem): number | undefined {
        return this.isLeagueOrVirtualGroup(item) ? undefined : item.count;
    }

    getIcon(item: TreeItem): string | undefined {
        return this.isLeagueOrVirtualGroup(item) ? item.icon : undefined;
    }

    getBorder(type: TopItemsType, lastItem: boolean): boolean {
        return type == TopItemsType.TopSports && lastItem;
    }

    getItemFavouriteId(item: TreeItem): string | number {
        if (item instanceof VirtualGroupTreeItem) {
            return item.favouriteId;
        } else if (item instanceof LeagueTreeItem) {
            return item.realCompetitionId || item.id;
        }

        return item.id;
    }

    isSeparator(item: TreeItem): boolean {
        return item instanceof TreeSeparatorItem;
    }

    ngOnDestroy(): void {
        this.widgetPayload$.complete();
    }

    private getPromotionsForType = (promoMap: PromotionMap, type: TopItemsType) => {
        return type === TopItemsType.TopSports ? promoMap.sports : type === TopItemsType.TopCompetitions ? promoMap.competitions : {};
    };

    isPriceBoost(isCountPresent: boolean): boolean {
        return this.priceBoostConfig.isEnabled && isCountPresent;
    }
}
